import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  Info,
  BugReport,
  Lightbulb,
  Speed,
  Timeline,
  Help,
  CheckCircle,
  Warning,
  Error,
  Close,
} from '@mui/icons-material';

interface TicketingUserGuideProps {
  open: boolean;
  onClose: () => void;
}

const TicketingUserGuide: React.FC<TicketingUserGuideProps> = ({ open, onClose }) => {
  const [expandedSection, setExpandedSection] = useState<string | false>('panel1');

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Help color="primary" />
            <Typography variant="h6">Ticketing System User Guide</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" paragraph>
          Learn how to effectively report issues and track their resolution. This guide helps you understand what gets fixed automatically vs. what requires discussion.
        </Typography>

        {/* Quick Overview */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Quick Tip:</strong> Be specific, provide screenshots, and include steps to reproduce for faster resolution!
          </Typography>
        </Alert>

        {/* Automatic vs Manual Fixes */}
        <Accordion expanded={expandedSection === 'panel1'} onChange={handleAccordionChange('panel1')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Speed color="success" />
              <Typography variant="subtitle1">🚨 Automatic Response Issues</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              These issues are typically resolved quickly without additional charges:
            </Typography>
            
            <List dense>
              <ListItem>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <ListItemText 
                  primary="UI/UX Issues" 
                  secondary="Broken layouts, misaligned elements, missing icons" 
                />
              </ListItem>
              <ListItem>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <ListItemText 
                  primary="Typos & Text Errors" 
                  secondary="Spelling mistakes, grammatical issues" 
                />
              </ListItem>
              <ListItem>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <ListItemText 
                  primary="Form Validation" 
                  secondary="Missing validation rules, error messages" 
                />
              </ListItem>
              <ListItem>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <ListItemText 
                  primary="Navigation Issues" 
                  secondary="Broken links, menu items not working" 
                />
              </ListItem>
              <ListItem>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <ListItemText 
                  primary="Data Display Problems" 
                  secondary="Incorrect formatting, missing information" 
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                ✅ Examples of Auto-Fix Issues:
              </Typography>
              <Typography variant="caption" component="div">
                • "Dashboard charts are not loading properly"<br/>
                • "There's a typo in the user registration form"<br/>
                • "Export button is not working on reports page"<br/>
                • "Password reset email is not being sent"<br/>
                • "Cannot login with correct credentials"<br/>
                • "Forgot password link shows 404 error"
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Authentication & Email Issues */}
        <Accordion expanded={expandedSection === 'panel2a'} onChange={handleAccordionChange('panel2a')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Error color="error" />
              <Typography variant="subtitle1">📧 Login & Email Issue Examples</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Detailed examples for common authentication and email problems:
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" color="error.main">
                🔐 Login Problems:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
                <Typography variant="caption" component="div">
                  <strong>Subject:</strong> Cannot login with correct credentials<br/>
                  <strong>Description:</strong> Page refreshes and returns to login screen<br/>
                  <strong>Key Info:</strong> Browser version, last successful login, tried clearing cache
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" color="error.main">
                📧 Email Not Sending:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
                <Typography variant="caption" component="div">
                  <strong>Subject:</strong> Email notifications not working<br/>
                  <strong>Description:</strong> System shows "Email sent" but nothing arrives<br/>
                  <strong>Key Info:</strong> Email provider, checked spam folder, affected users
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" color="error.main">
                🔑 Forgot Password Issues:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
                <Typography variant="caption" component="div">
                  <strong>Subject:</strong> Forgot password link not working<br/>
                  <strong>Description:</strong> 404 error or reset email not received<br/>
                  <strong>Key Info:</strong> Wait time tested, spam folder checked, multiple attempts
                </Typography>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Quick Template:</strong> Include browser info, email provider, what you've tried, and when it last worked.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Requires Discussion */}
        <Accordion expanded={expandedSection === 'panel3'} onChange={handleAccordionChange('panel3')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning color="warning" />
              <Typography variant="subtitle1">🔄 Requires Discussion & Agreement</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              These issues need detailed analysis, discussion, and may involve additional costs:
            </Typography>
            
            <List dense>
              <ListItem>
                <Warning color="warning" sx={{ mr: 1 }} />
                <ListItemText 
                  primary="Major Feature Requests" 
                  secondary="New modules, third-party integrations, complex reporting" 
                />
              </ListItem>
              <ListItem>
                <Warning color="warning" sx={{ mr: 1 }} />
                <ListItemText 
                  primary="Architecture Changes" 
                  secondary="Database modifications, workflow changes, security updates" 
                />
              </ListItem>
              <ListItem>
                <Warning color="warning" sx={{ mr: 1 }} />
                <ListItemText 
                  primary="System Integrations" 
                  secondary="API connections, external service integrations" 
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                🔄 Examples Requiring Discussion:
              </Typography>
              <Typography variant="caption" component="div">
                • "Need complete project management module with Gantt charts"<br/>
                • "Integrate with Salesforce CRM for customer sync"<br/>
                • "Implement real-time chat functionality"
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* How to Fill Form */}
        <Accordion expanded={expandedSection === 'panel4'} onChange={handleAccordionChange('panel4')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <BugReport color="primary" />
              <Typography variant="subtitle1">📝 How to Fill the Ticket Form</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Follow these steps for effective ticket submission:
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="1. Subject Line" 
                  secondary="Be specific, include module name (e.g., 'Dashboard charts not loading')" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="2. Description" 
                  secondary="Include steps to reproduce, expected behavior, current behavior" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="3. Attachments" 
                  secondary="Screenshots highly recommended, error logs, supporting files" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="4. Additional Info" 
                  secondary="Browser version, user role, frequency, business impact" 
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                📋 Description Template:
              </Typography>
              <Typography variant="caption" component="pre" sx={{ fontSize: '11px', mt: 1 }}>
{`**Issue Type:** Bug / Feature Request
**Affected Area:** [Module/Page Name]
**Current Behavior:** [What's happening now]
**Expected Behavior:** [What should happen]
**Steps to Reproduce:**
1. Go to [page]
2. Click on [button]
3. See error occur
**Impact:** [Critical/Medium/Low]`}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Status Tracking */}
        <Accordion expanded={expandedSection === 'panel5'} onChange={handleAccordionChange('panel5')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Timeline color="info" />
              <Typography variant="subtitle1">📊 Tracking Ticket Status</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Understand what each status means and expected response times:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="Open" color="error" size="small" />
                <Typography variant="body2">
                  <strong>New ticket, awaiting review</strong> - Response within 24 hours
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="In Progress" color="warning" size="small" />
                <Typography variant="body2">
                  <strong>Support team is working on it</strong> - Resolution in 2-5 days
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="Closed" color="success" size="small" />
                <Typography variant="body2">
                  <strong>Issue resolved</strong> - Complete
                </Typography>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Actual Status Flow:</strong> Open → In Progress → Closed
              </Typography>
            </Alert>

            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>How to Track:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Submit ticket and get ticket number in system interface" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Receive email notifications for status changes" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Log in and check 'My Tickets' tab" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Use your ticket number (e.g., TK-2024-001)" />
              </ListItem>
            </List>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                📧 Email Notification Timeline:
              </Typography>
              <Typography variant="caption" component="div">
                • Ticket Submitted: ❌ No email to user<br/>
                • Status Changed: ✅ Email sent to user<br/>
                • Response Added: ✅ Email sent to user<br/>
                • Ticket Closed: ✅ Email sent to user
              </Typography>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                <strong>Important:</strong> Check the system for your ticket number immediately after submission. Don't wait for email confirmation.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Good Examples */}
        <Accordion expanded={expandedSection === 'panel6'} onChange={handleAccordionChange('panel6')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Lightbulb color="secondary" />
              <Typography variant="subtitle1">✨ Examples of Good Tickets</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Well-written tickets get resolved faster:
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                ✅ Perfect Bug Report:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
                <Typography variant="caption" component="div">
                  <strong>Subject:</strong> Dashboard charts display "No Data" despite having records<br/>
                  <strong>Description:</strong> All dashboard charts show "No Data Available" even though we have 50+ projects. Started after Oct 15 update.<br/>
                  <strong>Steps:</strong> 1) Login as Project Manager, 2) Navigate to Dashboard, 3) Observe empty charts<br/>
                  <strong>Impact:</strong> Critical - Management cannot view performance metrics
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                ❌ What to Avoid:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, mt: 1 }}>
                <Typography variant="caption" component="div">
                  • Subject: "Not working" / "Help" / "Urgent"<br/>
                  • Description: "Fix it" / "The system is broken"<br/>
                  • No steps to reproduce, no error messages, no context
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Quick Tips */}
        <Accordion expanded={expandedSection === 'panel7'} onChange={handleAccordionChange('panel7')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info color="info" />
              <Typography variant="subtitle1">💡 Pro Tips for Faster Resolution</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="One Issue Per Ticket" 
                  secondary="Don't combine multiple problems" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Provide Business Context" 
                  secondary="'This blocks monthly reporting' helps prioritize" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Include Screenshots" 
                  secondary="Visual evidence is extremely helpful" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Check Recent Updates" 
                  secondary="Did the issue start after a recent change?" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Try Different Browser" 
                  secondary="Helps identify if it's browser-specific" 
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it, thanks!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketingUserGuide;
